import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { SearchBar } from "@/components/SearchBar";
import { DepartureBoard } from "@/components/DepartureBoard";
import { BookingDialog } from "@/components/BookingDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, X } from "lucide-react";
import { Seo } from "@/components/Seo";

const FACILITY_OPTIONS = ["AC", "WiFi", "Charger", "Reclining Seat", "Air Mineral", "Service Makan"];
const VEHICLE_OPTIONS = ["Hiace", "Innova", "Avanza"];

export default function SearchResults({ forcedService = "" } = {}) {
  const [params] = useSearchParams();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("rating");
  const [booking, setBooking] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [facilities, setFacilities] = useState([]);
  const [vehicle, setVehicle] = useState("all");
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const origin = params.get("origin") || "";
  const destination = params.get("destination") || "";
  const date = params.get("date") || "";
  const service_type = forcedService || params.get("service_type") || "";
  const svcLabel = service_type === "charter" ? "Charter Mobil" : service_type === "door_to_door" ? "Travel Door to Door" : "Hasil Pencarian";

  const buildQuery = (pg) => {
    const q = new URLSearchParams();
    if (origin) q.set("origin", origin);
    if (destination) q.set("destination", destination);
    if (date) q.set("date", date);
    if (service_type) q.set("service_type", service_type);
    q.set("sort", sort);
    if (facilities.length) q.set("facilities", facilities.join(","));
    if (vehicle !== "all") q.set("vehicle", vehicle);
    if (verifiedOnly) q.set("verified", "true");
    q.set("page", String(pg));
    q.set("limit", "50");
    return q;
  };

  useEffect(() => {
    setLoading(true);
    setPage(1);
    api.get(`/schedules/search?${buildQuery(1).toString()}`)
      .then((r) => { setSchedules(r.data.items || []); setHasMore(!!r.data.has_more); })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, destination, date, service_type, sort, facilities, vehicle, verifiedOnly]);

  const loadMore = () => {
    const next = page + 1;
    setLoadingMore(true);
    api.get(`/schedules/search?${buildQuery(next).toString()}`)
      .then((r) => { setSchedules((prev) => [...prev, ...(r.data.items || [])]); setHasMore(!!r.data.has_more); setPage(next); })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const toggleFacility = (f) =>
    setFacilities((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  const clearFilters = () => { setFacilities([]); setVehicle("all"); setVerifiedOnly(false); };
  const activeCount = facilities.length + (vehicle !== "all" ? 1 : 0) + (verifiedOnly ? 1 : 0);

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={origin && destination ? `Travel ${origin} ke ${destination} — Hasil Pencarian | Travele` : "Hasil Pencarian Travel | Travele"}
        description="Hasil pencarian jadwal travel door to door & charter drop off di Digital Terminal Travele."
        noindex
      />
      <Navbar />
      <div className="border-b border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
          <SearchBar initialService={service_type || "door_to_door"} initialOrigin={origin} initialDestination={destination} />
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
              {origin && destination ? `${origin} → ${destination}` : svcLabel}
            </h1>
            <p className="text-sm text-muted-foreground">
              {loading ? "Memuat..." : `${schedules.length} jadwal ditemukan`}{date ? ` · ${date}` : ""}
            </p>
          </div>
          <div className="w-48">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rating">Rating Tertinggi</SelectItem>
                <SelectItem value="price_asc">Harga Terendah</SelectItem>
                <SelectItem value="price_desc">Harga Tertinggi</SelectItem>
                <SelectItem value="time_asc">Jam Paling Awal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
          {/* Filter panel */}
          <aside className="h-fit border border-border bg-card p-5" data-testid="filter-panel">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold">
                <SlidersHorizontal className="h-4 w-4 text-primary" /> Filter
              </h2>
              {activeCount > 0 && (
                <Button data-testid="clear-filters" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={clearFilters}>
                  <X className="mr-1 h-3 w-3" /> Reset ({activeCount})
                </Button>
              )}
            </div>

            <div className="mb-5">
              <Label className="mb-2 block text-xs uppercase tracking-wide text-muted-foreground">Jenis Armada</Label>
              <Select value={vehicle} onValueChange={setVehicle}>
                <SelectTrigger data-testid="filter-vehicle"><SelectValue placeholder="Semua armada" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Armada</SelectItem>
                  {VEHICLE_OPTIONS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between border-t border-border pt-4">
              <Label htmlFor="verified-switch" className="text-sm">Driver Terverifikasi</Label>
              <Switch id="verified-switch" data-testid="filter-verified" checked={verifiedOnly} onCheckedChange={setVerifiedOnly} />
            </div>
          </aside>

          {/* Results */}
          <div>
            <DepartureBoard schedules={schedules} onBook={setBooking} showRoute={!destination} />
            {hasMore && (
              <div className="mt-6 flex justify-center">
                <Button data-testid="load-more-btn" variant="outline" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? "Memuat..." : "Muat lebih banyak"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
      <WhatsAppFloat />
      <BookingDialog schedule={booking} open={!!booking} onClose={() => setBooking(null)} />
    </div>
  );
}
